import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { DollarSign, Package, ShoppingBag, CreditCard, TrendingUp } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { key: 'today', label: 'Today', group: 'day' },
  { key: '7d', label: '7 Days', group: 'day' },
  { key: '30d', label: '30 Days', group: 'day' },
  { key: '90d', label: '3 Months', group: 'week' },
  { key: '365d', label: '1 Year', group: 'month' },
  { key: 'all', label: 'All Time', group: 'month' },
];

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

export const AnalyticsPanel = () => {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async (p) => {
    setLoading(true);
    try {
      const group = PERIODS.find(x => x.key === p)?.group || 'day';
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/analytics?period=${p}&group_by=${group}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(period); }, [period]);

  const s = data?.summary || { revenue: 0, units_sold: 0, orders: 0, prepaid: 0, cod: 0 };
  const paymentMix = [
    { name: 'Prepaid', value: s.prepaid, fill: '#C4B5A0' },
    { name: 'COD', value: s.cod, fill: '#8B7D6B' },
  ];

  return (
    <Card className="bg-white border-none shadow-lg p-6 mb-8" data-testid="analytics-panel">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-seasons text-[#2C2C2C]">Sales Analytics</h2>
          <p className="text-sm font-montserrat text-[#8B7D6B]">Revenue, units sold, and payment mix</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="period-selector">
          {PERIODS.map(p => (
            <Button
              key={p.key}
              size="sm"
              variant={period === p.key ? 'default' : 'outline'}
              data-testid={`period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              className={period === p.key ? 'bg-[#C4B5A0] hover:bg-[#B0A090] text-white' : 'border-[#E8E5E0] text-[#2C2C2C]'}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <MetricTile icon={<DollarSign className="h-5 w-5 text-green-600" />} bg="bg-green-50" label="Revenue" value={fmtINR(s.revenue)} testid="metric-revenue" />
        <MetricTile icon={<Package className="h-5 w-5 text-[#C4B5A0]" />} bg="bg-[#F5F3F0]" label="Units Sold" value={s.units_sold} testid="metric-units" />
        <MetricTile icon={<ShoppingBag className="h-5 w-5 text-[#C4B5A0]" />} bg="bg-[#F5F3F0]" label="Orders" value={s.orders} testid="metric-orders" />
        <MetricTile icon={<CreditCard className="h-5 w-5 text-blue-600" />} bg="bg-blue-50" label="Prepaid" value={s.prepaid} testid="metric-prepaid" />
        <MetricTile icon={<TrendingUp className="h-5 w-5 text-orange-600" />} bg="bg-orange-50" label="COD" value={s.cod} testid="metric-cod" />
      </div>

      {loading && <p className="text-center text-sm text-[#8B7D6B]">Loading...</p>}

      {/* Revenue + Units time series */}
      {data && data.timeseries && data.timeseries.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-[#FAF9F7] rounded-lg p-4" data-testid="chart-revenue">
            <h3 className="font-seasons text-lg text-[#2C2C2C] mb-2">Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.timeseries} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D5" />
                <XAxis dataKey="date" stroke="#8B7D6B" fontSize={11} />
                <YAxis stroke="#8B7D6B" fontSize={11} tickFormatter={(v) => `₹${v}`} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: '#fff', border: '1px solid #E8E2D5', fontFamily: 'monospace' }} />
                <Area type="monotone" dataKey="revenue" stroke="#C4B5A0" fill="#C4B5A0" fillOpacity={0.35} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-[#FAF9F7] rounded-lg p-4" data-testid="chart-units">
            <h3 className="font-seasons text-lg text-[#2C2C2C] mb-2">Units Sold</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.timeseries} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D5" />
                <XAxis dataKey="date" stroke="#8B7D6B" fontSize={11} />
                <YAxis stroke="#8B7D6B" fontSize={11} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E8E2D5' }} />
                <Bar dataKey="units" fill="#8B7D6B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {data && data.timeseries && data.timeseries.length === 0 && !loading && (
        <div className="text-center py-10 text-[#8B7D6B] font-montserrat">
          No paid orders in this period yet.
        </div>
      )}

      {/* Payment mix + top products */}
      {data && (s.prepaid + s.cod) > 0 && (
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div className="bg-[#FAF9F7] rounded-lg p-4" data-testid="chart-payment-mix">
            <h3 className="font-seasons text-lg text-[#2C2C2C] mb-2">Prepaid vs COD</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={paymentMix} layout="vertical" margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D5" />
                <XAxis type="number" stroke="#8B7D6B" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#8B7D6B" fontSize={12} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E8E2D5' }} />
                <Legend />
                <Bar dataKey="value" name="Orders">
                  {paymentMix.map((e, i) => <rect key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {data.top_products && data.top_products.length > 0 && (
            <div className="bg-[#FAF9F7] rounded-lg p-4" data-testid="top-products">
              <h3 className="font-seasons text-lg text-[#2C2C2C] mb-2">Top Products</h3>
              <div className="space-y-2">
                {data.top_products.slice(0, 6).map((p, i) => (
                  <div key={p.product_id} className="flex justify-between items-center text-sm font-montserrat">
                    <span className="truncate pr-3 text-[#2C2C2C]">{i + 1}. {p.name || `#${p.product_id}`}</span>
                    <div className="flex gap-4 text-[#8B7D6B] whitespace-nowrap">
                      <span>{p.units} units</span>
                      <span className="text-[#2C2C2C] font-semibold">{fmtINR(p.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const MetricTile = ({ icon, bg, label, value, testid }) => (
  <Card className="bg-white border border-[#F0EDE5] p-3" data-testid={testid}>
    <div className="flex items-center gap-2">
      <div className={`${bg} p-2 rounded-full`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] text-[#8B7D6B] font-montserrat uppercase tracking-wider">{label}</p>
        <p className="text-lg font-seasons text-[#2C2C2C] truncate">{value}</p>
      </div>
    </div>
  </Card>
);
